import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import SectionHead from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";

const IMGS = {
  web: "https://images.unsplash.com/photo-1686061592689-312bbfb5c055?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHszfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjB0ZWNobm9sb2d5JTIwZGFzaGJvYXJkJTIwQUklMjBjb252ZXJzYXRpb24lMjB3ZWJzaXRlJTIwaW50ZXJmYWNlfGVufDB8fHx8MTc5MjQ5ODcyNnww&ixlib=rb-4.1.0&q=85",
  voice: "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjB0ZWNobm9sb2d5JTIwZGFzaGJvYXJkJTIwQUklMjBjb252ZXJzYXRpb24lMjB3ZWJzaXRlJTIwaW50ZXJmYWNlfGVufDB8fHx8MTc5MjQ5ODcyNnww&ixlib=rb-4.1.0&q=85",
  whatsapp: "https://images.pexels.com/photos/30530409/pexels-photo-30530409.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  chat: "https://images.pexels.com/photos/16027823/pexels-photo-16027823.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  social: "https://images.unsplash.com/photo-1763718528755-4bca23f82ac3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjB0ZWNobm9sb2d5JTIwZGFzaGJvYXJkJTIwQUklMjBjb252ZXJzYXRpb24lMjB3ZWJzaXRlJTIwaW50ZXJmYWNlfGVufDB8fHx8MTc5MjQ5ODcyNnww&ixlib=rb-4.1.0&q=85",
};

const SERVICES = [
  {
    id: "website-development",
    badge: "DIGITAL FOUNDATION",
    title: "Website Development",
    headline: "Websites That Work as Hard as Your Business.",
    desc: "A well-designed website strengthens your credibility, showcases your services professionally, and turns visitors into qualified enquiries — every hour of the day.",
    img: IMGS.web,
    alt: "Professional business website mockup on modern devices",
    features: ["Professional corporate websites", "Business and service websites", "Product and catalogue websites", "Lead-generation landing pages", "Enquiry and requirement forms", "WhatsApp integration", "Mobile-responsive design", "SEO-friendly structure", "Website analytics integration", "Contact forms and lead capture", "Optional CRM and automation integrations", "Website maintenance and support"],
  },
  {
    id: "ai-voice-agent",
    badge: "AI VOICE AUTOMATION",
    title: "AI Voice Agent",
    headline: "Never Miss an Opportunity to Connect.",
    desc: "Your AI voice agent answers customer calls even outside working hours — greeting callers, answering questions, qualifying leads, and booking callbacks — so every ring becomes a business opportunity instead of a missed one.",
    img: IMGS.voice,
    alt: "AI voice agent call telemetry interface",
    features: ["AI-powered incoming call answering", "Automated customer FAQs", "Business information and service explanations", "Lead qualification through conversations", "Customer enquiry capture", "Appointment and callback requests", "Call summaries and enquiry records", "Call history and reporting, where supported", "Human handover for complex enquiries", "Optional outbound calling, subject to platform and consent requirements"],
    note: "AI Voice Agent Smart Calling Plans start at ₹25,000 for 3,000 calling minutes and scale up to 50,000 minutes — see the full plan table on the Pricing page. The agent handles calls 24/7, speaks all major Indian languages, books appointments, and captures and qualifies leads. Actual calling capabilities, languages, integrations, and usage limits depend on the selected plan and technology provider.",
  },
  {
    id: "whatsapp-ai",
    badge: "INSTANT MESSAGING",
    title: "AI Agents for WhatsApp",
    headline: "Turn WhatsApp Conversations into Business Opportunities.",
    desc: "Your customers already message businesses on WhatsApp. Our AI agent replies instantly, shares the right information, and captures every lead — built on supported WhatsApp Business Platform integrations and aligned with applicable messaging policies and consent requirements.",
    img: IMGS.whatsapp,
    alt: "WhatsApp business AI automated chat conversation",
    features: ["Automated responses to customer enquiries", "Product and service information", "Frequently asked question handling", "Lead collection", "Customer requirement qualification", "Appointment and callback requests", "Follow-up automation", "Human agent handover", "Integration with supported CRM systems", "Conversation history and reporting, where available"],
  },
  {
    id: "website-chat",
    badge: "ON-SITE CONVERSION",
    title: "AI Website Chat Agent",
    headline: "Your Digital Assistant, Available Around the Clock.",
    desc: "A chat agent on your website that welcomes visitors, answers questions, explains your services, and collects requirements — so every visitor can start a conversation immediately or submit their requirements through a form.",
    img: IMGS.chat,
    alt: "Intelligent website chat assistant widget",
    features: ["Website chat widget", "Automated answers to common questions", "Service and product explanations", "Customer requirement collection", "Lead qualification", "Contact information capture", "Demo and consultation requests", "Human support handover", "Optional knowledge-base integration", "Optional CRM integration"],
  },
  {
    id: "social-ai",
    badge: "SOCIAL SELLING",
    title: "Instagram & Facebook AI Agents",
    headline: "Stay Connected Across Your Social Channels.",
    desc: "Respond to social messages automatically, qualify leads, and manage every enquiry from one place. Features depend on Meta platform permissions, connected business accounts, and available integrations.",
    img: IMGS.social,
    alt: "Instagram and Facebook messaging automation hub",
    features: ["Automated responses to supported messages", "Answers to frequently asked questions", "Service and product information", "Lead qualification", "Customer enquiry collection", "Follow-up workflows where supported", "Human handover", "Centralized enquiry management through supported integrations"],
  },
];

export default function Services() {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "Services — Website Development, AI Voice & Chat Agents | RIDGELINE DIGI PARTNER";
  }, []);

  return (
    <div className="pt-[84px]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <SectionHead eyebrow="OUR SERVICES" title="Five Solutions. One Connected Customer Experience."
            description="Each solution works brilliantly on its own — and even better together, as one automated front office for your business." />
        </div>
      </section>

      {SERVICES.map((s, i) => (
        <section key={s.id} id={s.id} className={`scroll-mt-24 ${i % 2 === 0 ? "bg-slate-950/40" : ""} border-t border-white/5`}>
          <div className={`max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center`}>
            <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
              <div className="relative">
                <div className="absolute -inset-6 bg-blue-600/15 blur-[70px] rounded-full" aria-hidden="true" />
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                  <img src={s.img} alt={s.alt} className="w-full h-[300px] sm:h-[360px] object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/80 via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 rounded-full bg-slate-950/80 border border-white/10 px-3 py-1 font-mono2 text-[10px] tracking-[0.25em] text-cyan-400">
                    {s.badge}
                  </span>
                </div>
              </div>
            </Reveal>
            <div className={i % 2 === 1 ? "lg:order-1" : ""}>
              <Reveal>
                <p className="font-mono2 text-xs tracking-[0.25em] text-blue-400">{s.title.toUpperCase()}</p>
                <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-50">
                  {s.headline}
                </h2>
                <p className="mt-4 text-base text-slate-400 leading-relaxed">{s.desc}</p>
              </Reveal>
              <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {s.features.map((f, j) => (
                  <Reveal key={f} delay={Math.min(j * 0.04, 0.4)}>
                    <div className="flex items-start gap-2.5">
                      <Check size={15} className="mt-1 shrink-0 text-cyan-400" />
                      <p className="text-sm text-slate-300">{f}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              {s.note && (
                <Reveal>
                  <p className="mt-5 rounded-xl border border-blue-500/25 bg-blue-600/10 px-4 py-3 text-xs leading-relaxed text-blue-200/80">
                    {s.note}
                  </p>
                </Reveal>
              )}
              <Reveal delay={0.15}>
                <button onClick={() => navigate(`/contact?service=${encodeURIComponent(s.title)}`)}
                  data-testid={`service-quote-btn-${s.id}`}
                  className="mt-7 rounded-full bg-blue-600 px-7 py-3 text-sm font-semibold text-white shadow-[0_0_24px_rgba(37,99,235,0.4)] transition-all duration-300 hover:bg-blue-500 hover:-translate-y-0.5">
                  Get a Quote
                </button>
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      <section className="border-t border-white/5 bg-slate-950/50">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
          <Reveal className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-50">Quick Enquiry</h2>
            <p className="mt-3 text-sm text-slate-400">Interested in one of these solutions? Send a quick enquiry and we will get back to you.</p>
          </Reveal>
          <EnquiryForm compact sourcePage="services" />
        </div>
      </section>
    </div>
  );
}
